import React, { Suspense } from "react";
import { Trans } from "react-i18next";
import { styled } from "goober";
import { LoadingSpinner } from "clutch/src/LoadingSpinner/LoadingSpinner.js";

import type { GameSymbol } from "@/app/games.mjs";
import ArticleLinks from "@/marketing/ArticleLinks.jsx";
import ArticleTile from "@/marketing/ArticleTile.jsx";

const Wrapper = styled("div")`
  container-type: inline-size;
  container-name: game-articles;
`;

const ArticleSection = styled("section")`
  --article-tile-width: 15rem;
  --article-tile-max-width: 26rem;
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  width: 100%;
  max-width: var(--sp-container);
  margin-inline: auto;

  @container game-articles (inline-size <= 900px) {
    display: grid;
  }

  .game-callouts-title {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: var(--sp-3);

    @container app (inline-size <= 500px) {
      display: unset;
      text-align: center;
    }
  }
  .game-callouts-subtitle {
    color: var(--shade1);

    @container app (inline-size <= 500px) {
      display: block;
    }
  }
  .game-callouts {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-3);

    @container app (inline-size <= 850px) {
      --article-tile-width: unset;
      --article-tile-max-width: unset;
      flex-direction: column;
      align-items: center;
    }
  }
  .game-callout {
    display: flex;
    width: 100%;

    min-width: var(--article-tile-width);
    max-width: var(--article-tile-max-width);

    &:hover {
      z-index: 2;
    }
  }
`;

export interface GameArticlesEntry {
  createdAt?: Date;

  background?: string;
  image?: string;

  title: Translation;
  subtitle: Translation;

  link?: {
    href: string;
    target?: string;
    text?: Translation;
  };

  links?: Array<{ href: string; text: Translation; Icon?: SVGComponent }>;

  Component?: React.FC;
  componentProps?: Record<string, unknown>;
}

type GameArticlesProps = {
  game: GameSymbol;
  articles: Array<GameArticlesEntry>;
};

export default function GameArticles({ game, articles }: GameArticlesProps) {
  return (
    <Wrapper>
      <ArticleSection>
        <h2 className="game-callouts-title type-large-title--bold">
          <Trans i18nKey="home:latestNews">
            The Latest
            <span className="game-callouts-subtitle type-title--bold">
              Take a look at what&apos;s new, right now.
            </span>
          </Trans>
        </h2>
        <ul className="game-callouts">
          {articles?.map(
            (
              {
                createdAt,
                title,
                subtitle,
                link,
                links,
                background,
                image,
                Component,
                componentProps,
              },
              i,
            ) => (
              <li key={i} className="game-callout">
                {Component ? (
                  <Suspense fallback={<LoadingSpinner $position="relative" />}>
                    {React.createElement(Component, componentProps ?? null)}
                  </Suspense>
                ) : (
                  <ArticleTile
                    createdAt={new Date(createdAt)}
                    game={game}
                    title={title}
                    subtitle={subtitle}
                    link={link}
                    background={background}
                    image={image}
                  >
                    {links ? <ArticleLinks links={links} /> : null}
                  </ArticleTile>
                )}
              </li>
            ),
          )}
        </ul>
      </ArticleSection>
    </Wrapper>
  );
}
